import { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { Loader2, ClipboardList, Camera, CheckCircle2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import ProofActionDialog from "../components/orders/ProofActionDialog";

export default function OpsStaffDashboard() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proofTarget, setProofTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: q } = await api.get("/hierarchy/ops/my-queue");
      setData(q);
    } catch (e) {
      toast.error("Failed to load queue");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    const onHubEvt = () => load();
    window.addEventListener("pickngo:hub_order_event", onHubEvt);
    return () => {
      clearInterval(id);
      window.removeEventListener("pickngo:hub_order_event", onHubEvt);
    };
  }, [load]);

  if (loading) return <div className="p-8 flex items-center gap-3"><Loader2 className="animate-spin h-6 w-6 text-orange-500" /> Loading queue...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="ops-staff-dashboard">
      <h1 className="text-3xl font-bold flex items-center gap-3">
        <ClipboardList className="h-7 w-7 text-slate-600" />
        My Queue
      </h1>
      <p className="text-slate-500 mt-1">Hi {user?.name} — your assigned packages for today.</p>
      {!data?.hub_id && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-800 mt-6">
          You aren&apos;t attached to a hub yet. Ask your Micro Hub Lead to add you.
        </div>
      )}
      {data?.hub_id && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6 mb-4">
            <Kpi label="Hub" value={data.hub_id} />
            <Kpi label="Worker type" value={data.worker_type || "—"} />
            <Kpi label="Today's items" value={data.count} />
          </div>
          <Section title="Today's queue">
            <Table>
              <TableHeader><TableRow><TableHead>Tracking</TableHead><TableHead>Status</TableHead><TableHead>Pickup</TableHead><TableHead>Delivery</TableHead><TableHead className="text-right">Proof</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.today || []).map((o) => (
                  <TableRow key={o.tracking_id}>
                    <TableCell className="font-mono text-xs">{o.tracking_id}</TableCell>
                    <TableCell><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{o.status}</span></TableCell>
                    <TableCell className="text-xs truncate max-w-[160px]">{typeof o.pickup_address === "string" ? o.pickup_address : (o.pickup_address?.address || "—")}</TableCell>
                    <TableCell className="text-xs truncate max-w-[160px]">{typeof o.delivery_address === "string" ? o.delivery_address : (o.delivery_address?.address || "—")}</TableCell>
                    <TableCell className="text-right">
                      <ProofCell order={o} hubId={data.hub_id} onPick={setProofTarget} />
                    </TableCell>
                  </TableRow>
                ))}
                {(data.today || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">Queue is empty.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Section>
          <Section title="In-transit through your hub" className="mt-6">
            <Table>
              <TableHeader><TableRow><TableHead>Tracking</TableHead><TableHead>Status</TableHead><TableHead>Weight</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.in_transit || []).slice(0, 25).map((o) => (
                  <TableRow key={o.tracking_id}>
                    <TableCell className="font-mono text-xs">{o.tracking_id}</TableCell>
                    <TableCell className="text-xs">{o.status}</TableCell>
                    <TableCell className="text-xs">{o.package_weight ? `${o.package_weight} kg` : "—"}</TableCell>
                  </TableRow>
                ))}
                {(data.in_transit || []).length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-6">Nothing in transit.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Section>
        </>
      )}

      <ProofActionDialog
        target={proofTarget}
        onClose={() => setProofTarget(null)}
        onDone={load}
      />
    </div>
  );
}

function ProofCell({ order, hubId, onPick }) {
  // Decide which proof action is relevant based on order's current location
  const showPack = (order.status === "at_origin_hub" || order.current_hub_id === hubId) && !order.packing_photo;
  const showArrival = (order.status === "at_dest_hub" || order.destination_hub_id === hubId) && !order.destination_arrival_photo;
  if (order.packing_photo && order.destination_arrival_photo) {
    return <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Done</span>;
  }
  if (showPack) {
    return (
      <Button size="sm" variant="outline" onClick={() => onPick({ order, kind: "packing" })} data-testid={`pack-btn-${order.tracking_id}`}>
        <Camera className="h-3.5 w-3.5 mr-1" /> Pack
      </Button>
    );
  }
  if (showArrival) {
    return (
      <Button size="sm" variant="outline" onClick={() => onPick({ order, kind: "destination_arrival" })} data-testid={`arrived-btn-${order.tracking_id}`}>
        <Camera className="h-3.5 w-3.5 mr-1" /> Arrived
      </Button>
    );
  }
  return <span className="text-xs text-slate-400">—</span>;
}

function Kpi({ label, value }) {
  return <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm"><div className="text-xs uppercase tracking-wider text-slate-500">{label}</div><div className="text-lg font-bold mt-1 break-all">{value}</div></div>;
}
function Section({ title, children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-slate-100 p-5 shadow-sm ${className}`}><h3 className="font-semibold text-slate-800 mb-3">{title}</h3>{children}</div>;
}
